import { Trans, useLingui } from '@lingui/react';
import { Text } from '@mindbaz/ui-v2';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import dashboardsIllusatration from 'assets/features/dashboards_illustration.svg';
import safesendIllusatration from 'assets/features/Ilustration_SafeSend.png';
import snapshotIllusatration from 'assets/features/snapshot_illustration.svg';
import {
  getDailyReportAccess,
  getDashboardsAccess,
  getSafeSendAccess,
} from 'packages/endpoints/access';
import { isAdmin } from 'packages/endpoints/common';
import { FEATURE_MESSAGES } from 'packages/locales/i18n';

import { Header, NewFeatureBanner, Footer, FeatureCard } from './Blocks/index';
import {
  ContainerFeatures,
  CustomText,
  PageFeatures,
} from './styledComponents';
import { TFeature, TFeatureConfig } from './types';

const FEATURES = {
  SAFESEND: 'safesend',
  DASHBOARD: 'dashboards',
  SNAPSHOT: 'snapshot',
} as const;

const NEW_FEATURE = FEATURES.SAFESEND;

const Features = () => {
  const { _ } = useLingui();
  const [activeMore, setActiveMore] = useState('');

  const { data: dailyReportAccess } = useQuery(['dailyreport-access'], () =>
    getDailyReportAccess()
  );

  const { data: dashboardsAccess } = useQuery(['dashboards-access'], () =>
    getDashboardsAccess()
  );

  const { data: isAdminUser } = useQuery(['is-admin'], () => isAdmin());

  const { data: safeSendAccess } = useQuery(
    ['safesend-access'],
    () => getSafeSendAccess(),
    {
      suspense: false,
    }
  );

  /**
   * true if there is temporary free access for the user.
   */
  const temporaryFreeSubscription = useMemo(() => {
    return {
      dashboards: dashboardsAccess.tempFreeAccess,
      dailyReport: dailyReportAccess.tempFreeAccess,
    };
  }, [dailyReportAccess, dashboardsAccess]);

  const titleHeader = [
    FEATURES.SAFESEND,
    FEATURES.DASHBOARD,
    FEATURES.SNAPSHOT,
  ];

  const FEATURES_CARD: TFeature[] = useMemo(() => {
    const FEATURES_CONFIG: TFeatureConfig[] = [
      {
        translateKey: FEATURES.SAFESEND,
        illustration: safesendIllusatration,
        hasUserAccessFeature: safeSendAccess && isAdminUser === true,
      },
      {
        translateKey: FEATURES.DASHBOARD,
        illustration: dashboardsIllusatration,
        hasUserAccessFeature:
          dashboardsAccess.userHasAccess && dashboardsAccess.isSuperAdmin,
      },
      {
        translateKey: FEATURES.SNAPSHOT,
        illustration: snapshotIllusatration,
        hasUserAccessFeature:
          dailyReportAccess.userHasAccess && dailyReportAccess.isSuperAdmin,
      },
    ];

    return FEATURES_CONFIG.map(
      ({ translateKey, illustration, hasUserAccessFeature }) => ({
        label: translateKey,
        title: _(FEATURE_MESSAGES[translateKey].title),
        description: _(FEATURE_MESSAGES[translateKey].description),
        bafflingProblem: _(FEATURE_MESSAGES[translateKey].bafflingProblem),
        goal: _(FEATURE_MESSAGES[translateKey].goal),
        activeFeatureCTA: _(FEATURE_MESSAGES[translateKey].activeFeatureCTA),
        hasUserAccessFeature,
        illustration,
      })
    );
  }, [
    _,
    dailyReportAccess.isSuperAdmin,
    dailyReportAccess.userHasAccess,
    dashboardsAccess.isSuperAdmin,
    dashboardsAccess.userHasAccess,
    isAdminUser,
    safeSendAccess,
  ]);

  const isNewFeature = (feature: string) => {
    return NEW_FEATURE === feature;
  };

  const handleClick = (label: string) => {
    setActiveMore(label);
    if (label === activeMore) setActiveMore('');
  };

  const generateFeatures = () => {
    return FEATURES_CARD.map((feature: TFeature) => (
      <div id={feature.label} key={feature.label}>
        <FeatureCard
          label={feature.label}
          title={feature.title}
          description={feature.description}
          bafflingProblem={feature.bafflingProblem}
          goal={feature.goal}
          activeFeatureCTA={feature.activeFeatureCTA}
          hasUserAccessFeature={feature.hasUserAccessFeature}
          isNewFeature={isNewFeature(feature.label)}
          illustration={feature.illustration}
          handleFeatureMoreClick={handleClick}
          activeMore={activeMore}
          temporaryFreeSubscription={temporaryFreeSubscription}
        />
      </div>
    ));
  };

  return (
    <PageFeatures>
      <Header navigations={titleHeader} />
      <NewFeatureBanner newFeature={NEW_FEATURE} />
      <ContainerFeatures>
        <CustomText className="mb-16 mt-48 title">
          <Text variant="h1" weight="bold" align="center">
            <Trans
              id="features.title"
              message="Solve the email deliverability puzzle with Rubixo"
            />
          </Text>
        </CustomText>
        <CustomText className="mb-48 subtitle">
          <Text variant="subtitle" align="center">
            <Trans
              id="features.description"
              message="Discover the features of Rubixo that are based on the analysis of
              16 billion emails routed by Mindbaz every year."
            />
          </Text>
        </CustomText>
        {generateFeatures()}
        <Footer />
      </ContainerFeatures>
    </PageFeatures>
  );
};

export default Features;
